// src/pages/Home.js
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Hero from '../components/Hero';
import Button from '../components/Button';
import Testimonials from '../components/Testimonials';

const HomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const ServicesSection = styled.section`
  padding: 5rem 2rem;
  background-color: #f8fafc;
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 3rem;
  color: ${props => props.theme.colors.primary};
  text-align: center;
  margin-bottom: 1rem;
  position: relative;
  
  &:after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background-color: ${props => props.theme.colors.secondary};
    margin: 1rem auto;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.4rem;
  color: ${props => props.theme.colors.lightText};
  text-align: center;
  max-width: 800px;
  margin: 0 auto 4rem;
  line-height: 1.6;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2.5rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(motion.div)`
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.4s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  
  &:hover {
    transform: translateY(-12px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  }
  
  img {
    width: 100%;
    height: 220px;
    object-fit: cover;
    background-color: ${props => props.theme.colors.background};
  }
  
  .content {
    padding: 2rem;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    
    h3 {
      color: ${props => props.theme.colors.primary};
      margin-bottom: 1rem;
      font-size: 1.5rem;
    }
    
    p {
      color: ${props => props.theme.colors.lightText};
      margin-bottom: 1.5rem;
      line-height: 1.6;
      flex-grow: 1;
    }
  }
`;

const ServiceButton = styled(Button)`
  width: 100%;
  text-align: center;
  font-weight: 600;
  margin-top: auto;
`;

const JoinSection = styled.section`
  padding: 5rem 2rem;
  background-color: white;
  text-align: center;
`;

const JoinContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  h2 { 
    color: #1e3a8a; 
    margin-bottom: 1rem; 
    font-size: 2.5rem;
  }
  p { 
    color: #64748b; 
    margin-bottom: 2rem; 
    font-size: 1.2rem; 
    line-height: 1.6;
  }
`;

const CTASection = styled.section`
  background: linear-gradient(to right, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  padding: 5rem 2rem;
  text-align: center;
`;

const CTAContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  line-height: 1.2;
`;

const CTAText = styled.p`
  font-size: 1.4rem;
  margin-bottom: 3rem;
  line-height: 1.6;
`;

const CTAButton = styled(Button)`
  padding: 1.2rem 3rem;
  font-size: 1.2rem;
  font-weight: 600;
  border-radius: 5px;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const services = [
  {
    id: "dementia-care",
    title: "Dementia Care",
    description: "Specialized support for individuals living with dementia across the West Midlands, focusing on dignity and quality of life.",
    image: "/images/dementia-care.jpg"
  },
  {
    id: "agency-staffing",
    title: "Agency Staffing",
    description: "Providing highly qualified registered nurses and healthcare assistants to care homes throughout the West Midlands.",
    image: "/images/agency-staffing.jpg"
  },
  {
    id: "live-in-care",
    title: "Live-In Care (24/7)",
    description: "Round-the-clock care with dedicated professionals living in your home, providing continuous support and safety.",
    image: "/images/live-in-care.jpg"
  },
  {
    id: "care-professionals",
    title: "Care Professionals",
    description: "Connecting compassionate, vetted care professionals with families in Willenhall, Wolverhampton, and beyond.",
    image: "/images/care-professionals.jpg"
  },
  {
    id: "personal-care",
    title: "Personal Care",
    description: "Dignified assistance with daily living, including hygiene and medication management, tailored to your routine.",
    image: "/images/personal-care.jpg"
  },
  {
    id: "mental-health",
    title: "Mental Health Support",
    description: "Compassionate, specialized care promoting emotional wellbeing and stability for our West Midlands clients.",
    image: "/images/mental-health.jpg"
  }
];

const Home = () => {
  return (
    <HomeContainer>
      <Hero />
      
      <ServicesSection>
        <SectionContainer>
          <SectionTitle>Our Services</SectionTitle>
          <SectionSubtitle>
            Providing a comprehensive range of personalized care and staffing solutions across the West Midlands.
          </SectionSubtitle>
          
          <ServicesGrid>
            {services.map((service, index) => (
              <ServiceCard
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <img 
                  src={service.image} 
                  alt={service.title}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.backgroundColor = '#f0f9ff';
                  }}
                />
                <div className="content">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <ServiceButton to={`/services/${service.id}`} variant="outline" color="secondary">
                    Learn More
                  </ServiceButton>
                </div>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </SectionContainer>
      </ServicesSection>

      <Testimonials />

      <JoinSection>
        <JoinContainer>
          <h2>Join Our Team</h2>
          <p>Are you a compassionate care professional looking for a rewarding career? We are always looking for dedicated individuals to join the L&J Agape family.</p>
          <Button to="/work-with-us" color="secondary">Apply Now</Button>
        </JoinContainer>
      </JoinSection>
      
      <CTASection>
        <CTAContainer>
          <CTATitle>Ready to Discuss Your Care Needs?</CTATitle>
          <CTAText>
            Serving Willenhall, Wolverhampton, and the wider West Midlands. Our team is here to help you find the right care solution.
            Contact us today for a free, no-obligation consultation.
          </CTAText>
          <CTAButton to="/contact" color="accent">Book a Free Assessment</CTAButton>
        </CTAContainer>
      </CTASection>
    </HomeContainer>
  );
};

export default Home;